// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useState } from 'react';
import moment from 'moment-timezone';
import { Box, useTheme } from '@mui/system';
import { Typography, Select, MenuItem, InputBase, InputAdornment, Divider, Button, Fade, CircularProgress, TextField } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { billingMethods, pricingDescription } from 'Utils/constants';
import { credits as formatCredits, dollars } from 'Utils/numberFormat';
import TextEditor from 'Components/TextEditor';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { AdjustOrgCreditsThunk, AssignPricingPlanThunk, GetOrgPastPlansThunk, GetOrgSubscriptionThunk, GetOrgThunk, UpdateCostPerAcreThunk, UpdateOrgThunk, UpdatePaygRateThunk } from 'Features/admin/adminThunk';
import { RootState } from 'Store';
import HistoryList from 'Components/HistoryList';
import DatePicker from './datePicker';
import PlanHistory from './planHistory';
import CommittedTier from './committedTier';
import LicenseFee from './licenseFee';
import ExpressAI from './expressAI';
import { nearmapTierOptions, nearmapTierValue } from './nearmapTier';
import PricePerCredit from './pricePerCredit';
import stylesDef from './styles';
import ErrorPricePerCreditDialog from '../ErrorPricePerCreditDialog';

moment.tz.setDefault('America/New_York');

export const SubscriptionInfo = () => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();
  const { imageryProviders } = useAppSelector(
    (state: RootState) => state.imagery,
  );
  const { user } = useAppSelector(
    (state: RootState) => state.auth,
  );
  const { org, updatingOrgPricingPlan } = useAppSelector(
    (state: RootState) => state.admin,
  );
  const { contractUrl, billingMethod } = org || {};
  // Nearmap's tiers only, and the account's tier — "no subscription" when it holds none (AUT-6630)
  const nearmapOptions = nearmapTierOptions(imageryProviders);
  const nearmapValue = nearmapTierValue(org?.imageryProviders, nearmapOptions);
  const pricingPlanExists = !!(org.currentPricingPlanInfo);
  // the one plan object (AUT-6571 Chunk 9): a Stripe subscription, a plan or a migrated plan, described the same way
  const plan = org?.plan || null;
  const paygRateHistory = org?.paygRateHistory || [];
  const paygOverrideActive = typeof paygRateHistory[0]?.rate === 'number';
  const creditAdjustmentHistory = org?.creditAdjustmentHistory || [];
  const planInfo = org?.currentPricingPlanInfo;
  const planIsPayg = planInfo?.name === 'no_commit';
  const planRemainingCredits = planInfo ? (planInfo.creditsPurchased || 0) - (planInfo.usedCredits || 0) - (planInfo.startingCredits || 0) : 0;
  const [openStartDatepicker, setOpenStartDatepicker] = useState(false);
  const [selectedStartDate, setSelectedStartDate] = useState(org.currentPricingPlanInfo?.startDate || moment(new Date()).format());
  const [selectedEndDate, setSelectedEndDate] = useState(org.currentPricingPlanInfo?.endDate || moment(new Date()).format());
  const [pricingPlanName, setPricingPlanName] = useState(org.currentPricingPlanInfo?.name || '');
  const [creditsPurchased, setCreditsPurchased] = useState(org.currentPricingPlanInfo?.creditsPurchased || 0);
  const [startingCredits, setStartingCredits] = useState(org.currentPricingPlanInfo?.startingCredits || 0);
  const [openEndDatepicker, setOpenEndDatepicker] = useState(false);
  const [editPricingPlan, setEditPricingPlan] = useState(false);
  const [showOrgPlanHistory, setShowOrgPlanHistory] = useState(false);
  const [planHistory, setPlanHistory] = useState<IOrgPastPlan[]>([]);

  const [paygEditMode, setPaygEditMode] = useState<'set' | 'revert' | null>(null);
  const [paygRate, setPaygRate] = useState('');
  const [paygNotes, setPaygNotes] = useState('');
  const [savingPaygRate, setSavingPaygRate] = useState(false);

  const [adjustingCredits, setAdjustingCredits] = useState(false);
  const [adjustDelta, setAdjustDelta] = useState('');
  const [adjustNotes, setAdjustNotes] = useState('');
  const [savingAdjustment, setSavingAdjustment] = useState(false);
  const [adjustError, setAdjustError] = useState('');

  const [loadingPricePerCredit, setLoadingPricePerCredit] = useState(false);
  const [openEstimates, setOpenEstimates] = useState<any[]>([]);
  const [showErrorPricePerCreditDialog, setShowErrorPricePerCreditDialog] = useState(false);

  // Credits live in one place per org (AUT-6106): the active plan row for a pre-pay / pre-commit org, the org
  // document for a Stripe-subscription org, nowhere for PAYG. The plan wins when both exist, as on the server.
  const subscriptionCredits = !pricingPlanExists && typeof plan?.credits?.total === 'number' ? plan.credits : null;
  let creditContext: 'plan' | 'organization' | null = null;
  if (pricingPlanExists && !planIsPayg) {
    creditContext = 'plan';
  } else if (subscriptionCredits) {
    creditContext = 'organization';
  }
  const contextBalance = creditContext === 'plan' ? planRemainingCredits : (subscriptionCredits?.remaining || 0);
  // the block's figures: the plan row's for a plan account, the plan object's for a subscription
  const blockCredits = creditContext === 'plan'
    ? { total: planInfo?.creditsPurchased ?? null, used: planInfo?.usedCredits ?? null, remaining: planRemainingCredits }
    : subscriptionCredits;
  const blockStartDate = pricingPlanExists ? selectedStartDate : plan?.startDate;
  const blockEndDate = pricingPlanExists ? selectedEndDate : plan?.endDate;
  const legacyPlanLabel = pricingDescription.find((tier) => tier.name === pricingPlanName)?.displayName;
  const planLabel = plan?.label ? `${plan.label}${plan.tier ? ` · ${plan.tier} (Credit Ladder)` : ''}` : legacyPlanLabel;
  const hasPlanBlock = pricingPlanExists || !!plan;
  // who invoices go to: the organization's point of contact (AUT-6571)
  const billingContact = org?.billingContact;
  const billingContactName = [billingContact?.firstName, billingContact?.lastName].filter(Boolean).join(' ');
  const billingContactLine = billingContact?.email ? [billingContactName, billingContact.email].filter(Boolean).join(' · ') : null;
  const parsedDelta = parseFloat(adjustDelta);
  const deltaIsValid = Number.isFinite(parsedDelta) && parsedDelta !== 0 && Math.abs(parsedDelta * 100 - Math.round(parsedDelta * 100)) < 1e-9;
  const newBalance = deltaIsValid ? contextBalance + parsedDelta : null;

  const handleChangeNearmap = (value: string) => {
    dispatch(UpdateOrgThunk({ _id: org._id, imageryProvider: value }));
  };

  const updateOrgContractUrl = (url: string) => {
    dispatch(UpdateOrgThunk({ _id: org._id, contractUrl: url }));
  };

  const handleChangeBillingMethod = (method: string) => {
    dispatch(UpdateOrgThunk({ _id: org._id, billingMethod: method }));
  };

  const resetPricingPlan = () => {
    if (org?.currentPricingPlanInfo) {
      const { startDate, endDate } = org.currentPricingPlanInfo;
      const orgPricingPlanName = org.currentPricingPlanInfo.name;
      const orgStartingCredits = org.currentPricingPlanInfo.startingCredits;
      const orgCreditsPurchased = org.currentPricingPlanInfo.creditsPurchased;
      setSelectedStartDate(startDate);
      setSelectedEndDate(endDate);
      setPricingPlanName(orgPricingPlanName);
      setCreditsPurchased(orgCreditsPurchased);
      setStartingCredits(orgStartingCredits);
    } else {
      setSelectedStartDate(moment(new Date()).format());
      setSelectedEndDate(moment(new Date()).format());
      setPricingPlanName('');
      setCreditsPurchased(0);
      setStartingCredits(0);
    }
  };

  const savePricingPlan = async () => {
    const pricingData = {
      startDate: selectedStartDate,
      pricingPlanName,
      startingCredits,
      ...(pricingPlanName !== 'no_commit' && { endDate: selectedEndDate, creditsPurchased }),
    };
    const resultSuccess = await dispatch(AssignPricingPlanThunk(pricingData));
    setEditPricingPlan(false);
    if (!resultSuccess) {
      resetPricingPlan();
    }
  };

  const cancelEditPricingPlan = async () => {
    setEditPricingPlan(false);
    resetPricingPlan();
  };

  const getOrgPlanHistory = async () => {
    const pastPlanData = await dispatch(GetOrgPastPlansThunk());
    if (pastPlanData?.length) {
      setPlanHistory(pastPlanData);
    }
    setShowOrgPlanHistory(true);
  };

  const onCancel = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setShowOrgPlanHistory(false);
    e.stopPropagation();
  };

  const getPlanLength = (startDate = blockStartDate, endDate = blockEndDate) => {
    const momentStartDate = moment(startDate);
    const momentEndDate = moment(endDate);
    const diffDuration = moment.duration(momentEndDate.diff(momentStartDate));
    let planLength = '';
    const diffYears = diffDuration.years();
    if (diffYears > 0) {
      planLength += `${diffYears} year(s)`;
    }
    const diffMonths = diffDuration.months();
    if (diffMonths > 0) {
      planLength += ` ${diffMonths} month(s)`;
    }
    const diffDays = diffDuration.days();
    if (diffDays > 0) {
      planLength += ` ${diffDays} day(s)`;
    }
    return planLength;
  };

  const handleUpdateCostPerAcre = async (value: string) => {
    setLoadingPricePerCredit(true);
    const result = await dispatch(UpdateCostPerAcreThunk(value));
    if (!result.success && result.errors && Object.keys(result.errors).length > 0) {
      setShowErrorPricePerCreditDialog(true);
      // We parse this errors to get the projects and orderIds
      const estimates = Object.keys(result.errors).map((key) => result.errors[key]).map((project) => JSON.parse(project));
      setOpenEstimates(estimates);
    }
    setLoadingPricePerCredit(false);
  };

  const cancelAdjustCredits = () => {
    setAdjustingCredits(false);
    setAdjustDelta('');
    setAdjustNotes('');
    setAdjustError('');
  };

  const saveAdjustment = async () => {
    setSavingAdjustment(true);
    setAdjustError('');
    const result = await dispatch(AdjustOrgCreditsThunk(parsedDelta, adjustNotes.trim()));
    if (result.success) {
      await dispatch(GetOrgThunk(org._id));
      await dispatch(GetOrgSubscriptionThunk(org._id));
      cancelAdjustCredits();
    } else {
      setAdjustError(result.message || 'Failed to adjust credits');
    }
    setSavingAdjustment(false);
  };

  const renderCreditAdjustments = () => (
    <>
      {!adjustingCredits && (
        <Box sx={styles.planButtonGroup}>
          <Button disabled={!user?.pricingAccess} sx={styles.planButton} onClick={() => setAdjustingCredits(true)} color="primary" variant="outlined">
            Adjust Credits
          </Button>
        </Box>
      )}
      {adjustingCredits && (
        <>
          <Box sx={styles.formContent}>
            <Typography>Credits to add (negative to remove)</Typography>
            <Box sx={styles.textFieldDiv}>
              <TextField
                id="adjustDelta"
                variant="standard"
                type="number"
                inputProps={{ step: '0.01' }}
                onChange={(e) => setAdjustDelta(e.target.value)}
                value={adjustDelta}
                sx={styles.textField}
              />
            </Box>
            <Typography>New balance</Typography>
            <Typography>{newBalance === null ? '—' : `${formatCredits(newBalance)} credits`}</Typography>
            <Typography>Notes (required)</Typography>
            <Box sx={styles.textFieldDiv}>
              <TextField
                id="adjustNotes"
                variant="standard"
                multiline
                minRows={2}
                onChange={(e) => setAdjustNotes(e.target.value)}
                value={adjustNotes}
                sx={styles.paygNotesField}
              />
            </Box>
          </Box>
          {adjustError && <Typography variant="body2" sx={styles.creditError}>{adjustError}</Typography>}
          <Box sx={styles.buttonGroup}>
            <Button
              sx={styles.planButton}
              disabled={!deltaIsValid || !adjustNotes.trim() || savingAdjustment}
              onClick={saveAdjustment}
              color="primary"
              variant="contained"
            >
              <Fade in={savingAdjustment}>
                <CircularProgress size={24} sx={styles.buttonProgress} />
              </Fade>
              <Fade in={!savingAdjustment}>
                <span>Save Adjustment</span>
              </Fade>
            </Button>
            <Button sx={styles.planButton} onClick={cancelAdjustCredits} variant="text">Cancel</Button>
          </Box>
        </>
      )}
      <HistoryList
        entries={creditAdjustmentHistory}
        getKey={(entry) => entry._id}
        renderRow={(entry) => (
          <Box sx={styles.paygHistoryRow}>
            <Typography variant="body2">{moment.utc(entry.createdAt).format('MM/DD/YYYY')}</Typography>
            <Typography variant="body2" sx={styles.creditHistoryDelta}>
              {`${entry.deltaCredits > 0 ? '+' : '−'}${formatCredits(Math.abs(entry.deltaCredits))} credits`}
            </Typography>
            <Typography variant="body2">{`balance ${formatCredits(entry.balanceAfterCredits)}`}</Typography>
            <Typography variant="body2">{entry.changedBy}</Typography>
            <Typography variant="body2" sx={styles.paygHistoryNotes}>{entry.notes}</Typography>
          </Box>
        )}
      />
    </>
  );

  const cancelPaygEdit = () => {
    setPaygEditMode(null);
    setPaygRate('');
    setPaygNotes('');
  };

  const savePaygRate = async () => {
    setSavingPaygRate(true);
    const rateToSave = paygEditMode === 'revert' ? null : parseFloat(paygRate);
    const result = await dispatch(UpdatePaygRateThunk(rateToSave, paygNotes.trim()));
    if (result.success) {
      await dispatch(GetOrgThunk(org._id));
      cancelPaygEdit();
    }
    setSavingPaygRate(false);
  };

  return (
    <>
      <Typography variant="h4" sx={styles.sectionTitle}>Subscription Info</Typography>
      <Divider sx={styles.divider} />
      <Box sx={styles.subscriptionDate}>
        <Typography>Nearmap Tier</Typography>
        {nearmapOptions.length > 0 && (
          <Select
            variant="outlined"
            input={<InputBase sx={styles.inputBase} />}
            value={nearmapValue}
            onChange={(e) => handleChangeNearmap(e.target.value)}
          >
            {
              nearmapOptions.map(({ name: ipName, _id }) => <MenuItem key={_id} value={_id}>{ipName}</MenuItem>)
            }
          </Select>
        )}
      </Box>
      <Box sx={styles.subscriptionDate}>
        <Typography>Contract URL</Typography>
        <TextEditor
          initialValue={contractUrl}
          updateValue={(value) => updateOrgContractUrl(value)}
          linkText
        />
      </Box>
      <Box sx={styles.subscriptionDate}>
        <Typography>Billing Method</Typography>
        <Select
          variant="outlined"
          input={<InputBase sx={styles.billingMethod} />}
          value={billingMethod || ''}
          onChange={(e) => handleChangeBillingMethod(e.target.value)}
        >
          {
            billingMethods.map((method: string) => <MenuItem key={method} value={method}>{method}</MenuItem>)
          }
        </Select>
      </Box>
      <LicenseFee />
      <PricePerCredit onUpdateCostPerAcre={handleUpdateCostPerAcre} loadingPricePerCredit={loadingPricePerCredit} />
      <Box>
        <Typography variant="h4" sx={styles.sectionTitle}>Plan Info</Typography>
        <Divider sx={styles.divider} />
        {(!editPricingPlan && hasPlanBlock) && (
          <Box sx={styles.listContent}>
            <Typography>Plan</Typography>
            <Typography>{planLabel}</Typography>
            {billingContactLine && (
              <>
                <Typography>Invoices go to</Typography>
                <Typography>{billingContactLine}</Typography>
              </>
            )}
            <Typography>Plan Start Date</Typography>
            <Typography>{blockStartDate ? moment(blockStartDate).format('LL') : '—'}</Typography>
            {!planIsPayg && (
              <>
                <Typography>Contract End Date</Typography>
                <Typography>{blockEndDate ? moment(blockEndDate).format('LL') : '—'}</Typography>
                <Typography>Plan Length</Typography>
                <Typography>{blockStartDate && blockEndDate ? getPlanLength() : '—'}</Typography>
              </>
            )}
            {pricingPlanExists && startingCredits >= 0 && (
              <>
                <Typography>Starting Credits</Typography>
                <Typography>{startingCredits}</Typography>
              </>
            )}
            {blockCredits && (
              <>
                <Typography>Credits Purchased</Typography>
                <Typography>{blockCredits.total === null ? 'N/A' : formatCredits(blockCredits.total)}</Typography>
                <Typography>Credits Used</Typography>
                <Typography>{blockCredits.used === null ? 'N/A' : formatCredits(blockCredits.used)}</Typography>
                <Typography>Credits Remaining</Typography>
                <Typography>{blockCredits.remaining === null ? 'N/A' : formatCredits(blockCredits.remaining)}</Typography>
              </>
            )}
            {pricingPlanName === 'no_commit' && (
              <>
                <Typography>PAYG Rate</Typography>
                <Typography>
                  {dollars(org.currentPricingPlanInfo?.rate ?? 0)}
                  {paygOverrideActive && (
                    <Typography component="span" variant="body2" sx={styles.paygOverrideBadge}>(org override)</Typography>
                  )}
                </Typography>
              </>
            )}
          </Box>
        )}
        {!editPricingPlan && (
          <Box sx={styles.planButtonGroup}>
            <Button disabled={!user?.pricingAccess} sx={styles.planButton} onClick={() => setEditPricingPlan(true)} color="primary" variant="contained">
              {pricingPlanExists ? 'Edit Plan' : 'Assign New Plan'}
            </Button>
            {hasPlanBlock && (
              <Button variant="text" color="primary" disableRipple sx={styles.historyButton} onClick={getOrgPlanHistory}>
                <AccessTimeIcon sx={styles.historyButtonIcon} />
                <Typography variant="h4" color="primary" sx={styles.historyButtonText}>View Plan History</Typography>
              </Button>
            )}
          </Box>
        )}
        {(!editPricingPlan && creditContext !== null) && renderCreditAdjustments()}
        {(!editPricingPlan && pricingPlanName === 'no_commit') && (
          <>
            {!paygEditMode && (
              <Box sx={styles.planButtonGroup}>
                <Button disabled={!user?.pricingAccess} sx={styles.planButton} onClick={() => setPaygEditMode('set')} color="primary" variant="outlined">
                  Edit PAYG Rate
                </Button>
                {paygOverrideActive && (
                  <Button disabled={!user?.pricingAccess} sx={styles.planButton} onClick={() => setPaygEditMode('revert')} variant="text">
                    Revert to Standard Rate
                  </Button>
                )}
              </Box>
            )}
            {paygEditMode && (
              <>
                <Box sx={styles.formContent}>
                  <Typography>New PAYG Rate</Typography>
                  {paygEditMode === 'set' ? (
                    <Box sx={styles.textFieldDiv}>
                      <TextField
                        id="paygRate"
                        variant="standard"
                        type="number"
                        InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                        onChange={(e) => setPaygRate(e.target.value)}
                        value={paygRate}
                        sx={styles.textField}
                      />
                    </Box>
                  ) : (
                    <Typography>Standard rate</Typography>
                  )}
                  <Typography>Notes (required)</Typography>
                  <Box sx={styles.textFieldDiv}>
                    <TextField
                      id="paygNotes"
                      variant="standard"
                      multiline
                      minRows={2}
                      onChange={(e) => setPaygNotes(e.target.value)}
                      value={paygNotes}
                      sx={styles.paygNotesField}
                    />
                  </Box>
                </Box>
                <Box sx={styles.buttonGroup}>
                  <Button
                    sx={styles.planButton}
                    disabled={!paygNotes.trim() || (paygEditMode === 'set' && !(parseFloat(paygRate) > 0)) || savingPaygRate}
                    onClick={savePaygRate}
                    color="primary"
                    variant="contained"
                  >
                    <Fade in={savingPaygRate}>
                      <CircularProgress size={24} sx={styles.buttonProgress} />
                    </Fade>
                    <Fade in={!savingPaygRate}>
                      <span>{paygEditMode === 'revert' ? 'Revert Rate' : 'Save Rate'}</span>
                    </Fade>
                  </Button>
                  <Button sx={styles.planButton} onClick={cancelPaygEdit} variant="text">Cancel</Button>
                </Box>
              </>
            )}
            <HistoryList
              entries={paygRateHistory}
              getKey={(entry) => entry._id}
              renderRow={(entry) => (
                <Box sx={styles.paygHistoryRow}>
                  <Typography variant="body2">{moment.utc(entry.createdAt).format('MM/DD/YYYY')}</Typography>
                  <Typography variant="body2">{typeof entry.rate === 'number' ? `${dollars(entry.rate)} per credit` : 'Standard rate'}</Typography>
                  <Typography variant="body2">{entry.changedBy}</Typography>
                  <Typography variant="body2" sx={styles.paygHistoryNotes}>{entry.notes}</Typography>
                </Box>
              )}
            />
            <Typography variant="body2" sx={styles.creditHelperText}>
              Pay-as-you-go accounts hold no credit balance. Assign a pre-pay or pre-commit plan to give this account credits.
            </Typography>
          </>
        )}
        {editPricingPlan && (
          <>
            <Box sx={styles.formContent}>
              <Typography>Pricing Plan Start Date</Typography>
              <Box>
                <DatePicker openDatepicker={openStartDatepicker} setOpenDatepicker={setOpenStartDatepicker} selectedDate={selectedStartDate} setSelectedDate={setSelectedStartDate} />
              </Box>
              {pricingPlanName !== 'no_commit' && (
                <>
                  <Typography>Contract End Date</Typography>
                  <Box>
                    <DatePicker openDatepicker={openEndDatepicker} setOpenDatepicker={setOpenEndDatepicker} selectedDate={selectedEndDate} setSelectedDate={setSelectedEndDate} />
                  </Box>
                </>
              )}
              <Typography>Pricing Tier</Typography>
              <Box>
                <Select
                  variant="outlined"
                  input={<InputBase sx={styles.inputBase} />}
                  value={pricingPlanName}
                  onChange={(e) => setPricingPlanName(e.target.value)}
                >
                  {pricingDescription.map((planOption) => (
                    <MenuItem value={planOption.name} key={planOption.name}>{planOption.displayName}</MenuItem>
                  ))}
                </Select>
              </Box>
              {planInfo?.pricingSource === 'ladder' && pricingPlanName !== 'no_commit' && (
                <Typography variant="body2" sx={styles.creditHelperText}>
                  {`Prices at the Credit Ladder tier ${planInfo.ladderTier === 'Standard' ? 'Entry' : planInfo.ladderTier} (${dollars(planInfo.rate)} per credit) — kept when you save. Change the tier in the Committed Tier panel.`}
                </Typography>
              )}
              <Typography>Starting Credits</Typography>
              <Box sx={styles.textFieldDiv}>
                <TextField
                  id="startingCredits"
                  variant="standard"
                  type="number"
                  inputProps={{ min: 0 }}
                  onChange={(e) => { setStartingCredits(parseFloat(e.target.value)); }}
                  value={startingCredits?.toString()}
                  sx={styles.textField}
                />
              </Box>
              {pricingPlanName !== 'no_commit' && (
                <>
                  <Typography>Credits Purchased</Typography>
                  <Box sx={styles.textFieldDiv}>
                    <TextField
                      id="creditsPurchased"
                      variant="standard"
                      type="number"
                      inputProps={{ min: 0 }}
                      onChange={(e) => { setCreditsPurchased(parseFloat(e.target.value)); }}
                      value={creditsPurchased?.toString()}
                      sx={styles.textField}
                    />
                  </Box>
                </>
              )}
            </Box>
            <Box sx={styles.buttonGroup}>
              <Button
                sx={styles.planButton}
                disabled={(!selectedStartDate || !pricingPlanName || (pricingPlanName !== 'no_commit' && (!selectedEndDate || !creditsPurchased)))}
                onClick={savePricingPlan}
                color="primary"
                variant="contained"
              >
                <Fade in={updatingOrgPricingPlan}>
                  <CircularProgress size={24} sx={styles.buttonProgress} />
                </Fade>
                <Fade in={!updatingOrgPricingPlan}>
                  <span>Save Changes</span>
                </Fade>
              </Button>
              <Button sx={styles.planButton} onClick={cancelEditPricingPlan} variant="text">Cancel</Button>
            </Box>
          </>
        )}
        <PlanHistory showDialog={showOrgPlanHistory} planHistory={planHistory} onCancel={onCancel} />
      </Box>
      <CommittedTier />
      <ExpressAI />
      <ErrorPricePerCreditDialog
        showErrorPricePerCreditDialog={showErrorPricePerCreditDialog}
        onClose={() => setShowErrorPricePerCreditDialog(false)}
        projects={openEstimates}
      />
    </>
  );
};

export default SubscriptionInfo;
